import Button from '@orn/shared-web/components/Button';

import { SettingsCard } from '../common';

import { formatPeriodEnd, summarizeBilling } from './billingSummary';

import type { SubscriptionInterval, SubscriptionStatus } from '../../../__generated__/globalTypes';
import type * as React from 'react';

export interface IBillingSubscription {
  status: SubscriptionStatus;
  isEntitled: boolean;
  interval: SubscriptionInterval;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  /** A scheduled interval change and when it lands, or nulls when none is pending. */
  pendingInterval: SubscriptionInterval | null;
  pendingIntervalAt: Date | null;
  paymentMethod: { institutionName: string | null; mask: string | null } | null;
}

export interface IBillingSettingsBaseProps {
  /** Null when the household has never subscribed. */
  subscription: IBillingSubscription | null;
  /**
   * Opens checkout. Absent while the billing gate is closed, which is what
   * keeps the entry point from appearing before checkout exists.
   */
  onSubscribe?: () => void;
  /**
   * Portal entry points shown to an entitled subscriber. Supplied by the
   * container so this component stays presentational.
   */
  manageMembership?: React.ReactNode;
  /**
   * Opens the payment-account switch. Offered only to an entitled subscriber;
   * absent while the billing gate is closed.
   */
  onSwitchAccount?: () => void;
}

// Keyed on the enum so a new interval demands an explicit label instead of
// silently borrowing the monthly fallback.
const INTERVAL_LABEL: Record<SubscriptionInterval, string> = {
  MONTHLY: 'Billed monthly',
  ANNUAL: 'Billed annually',
};

const PENDING_INTERVAL_LABEL: Record<SubscriptionInterval, string> = {
  MONTHLY: 'monthly billing',
  ANNUAL: 'annual billing',
};

/**
 * The "Billing" settings tab.
 *
 * Renders a lapsed subscription as well as a live one — a household that
 * canceled should read "canceled", not the never-subscribed state, so the
 * card distinguishes "no subscription yet" from "no longer subscribed".
 */
const BillingSettingsBase = ({
  subscription,
  onSubscribe,
  manageMembership,
  onSwitchAccount,
}: IBillingSettingsBaseProps) => (
  <div className="flex max-w-[736px] flex-col gap-5">
    <SettingsCard title="Plan & billing" subtitle="Your subscription and payment details.">
      {subscription === null ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Current plan</p>
            <p className="text-primary text-md font-medium">No subscription</p>
            <p className="text-tertiary text-sm">
              You don't have a subscription yet. Start one to keep using Mantle after your trial.
            </p>
          </div>
          {onSubscribe !== undefined && (
            <div>
              <Button size="md" onClick={onSubscribe}>
                Start subscription
              </Button>
            </div>
          )}
        </div>
      ) : (
        <BillingDetail
          subscription={subscription}
          onSubscribe={onSubscribe}
          manageMembership={manageMembership}
          onSwitchAccount={onSwitchAccount}
        />
      )}
    </SettingsCard>
  </div>
);

interface IBillingDetailProps {
  subscription: IBillingSubscription;
  onSubscribe?: () => void;
  manageMembership?: React.ReactNode;
  onSwitchAccount?: () => void;
}

const BillingDetail = ({ subscription, onSubscribe, manageMembership, onSwitchAccount }: IBillingDetailProps) => {
  const { planLabel, detail, periodEndMeaning } = summarizeBilling(subscription.status, subscription.cancelAtPeriodEnd);
  const { paymentMethod, currentPeriodEnd } = subscription;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Current plan</p>
        <p className="text-primary text-md font-medium">{planLabel}</p>
        <p className="text-tertiary text-sm">{detail}</p>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Billing</p>
        <p className="text-secondary text-sm">{INTERVAL_LABEL[subscription.interval]}</p>
        {/* A membership ending at period end never reaches the scheduled
            switch — announcing it above "Access ends" would promise a change
            that will not happen. */}
        {subscription.pendingInterval !== null &&
          subscription.pendingIntervalAt !== null &&
          periodEndMeaning !== 'endsAccess' && (
            <p className="text-tertiary text-sm">
              {'Switches to '}
              {PENDING_INTERVAL_LABEL[subscription.pendingInterval]}
              {' on '}
              {formatPeriodEnd(subscription.pendingIntervalAt)}
            </p>
          )}
        {currentPeriodEnd !== null && periodEndMeaning !== null && (
          <p className="text-tertiary text-sm">
            {periodEndMeaning === 'endsAccess' ? 'Access ends ' : 'Next charge '}
            {formatPeriodEnd(currentPeriodEnd)}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Payment account</p>
        {paymentMethod === null ? (
          <p className="text-tertiary text-sm">No payment account on file.</p>
        ) : (
          <p className="text-secondary text-sm">
            {paymentMethod.institutionName ?? 'Bank account'}
            {paymentMethod.mask !== null && ` ••${paymentMethod.mask}`}
          </p>
        )}
        {subscription.isEntitled && onSwitchAccount !== undefined && (
          <div>
            <Button size="md" color="link-gray" onClick={onSwitchAccount}>
              Switch payment account
            </Button>
          </div>
        )}
      </div>

      {/* A lapsed household can subscribe again; a live one manages through
          the portal entry points, with the payment-account switch above. */}
      {subscription.isEntitled
        ? manageMembership
        : onSubscribe !== undefined && (
            <div>
              <Button size="md" onClick={onSubscribe}>
                Start subscription
              </Button>
            </div>
          )}
    </div>
  );
};

BillingSettingsBase.displayName = 'BillingSettingsBase';

export default BillingSettingsBase;
